import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, EyeIcon, UsersIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { bursariesApi } from "../../api/hliLists";
import { errorMessageOf } from "../../api/scholarships";
import { Container, EmptyState, Loading, MetricCard, PageHeader, RowAction, Select, Switch, Table, Tabs, Tag } from "../ui";
import LoanRequestsTable from "./LoanRequestsTable";
import AdmittedStudentsTable from "./AdmittedStudentsTable";
import { BURSARIES_KEY, useYearParam } from "./shared";

export const INSTITUTIONS_PATH = "/bursaries/institutions";

const INSTITUTIONS_KEY = [BURSARIES_KEY, "institutions"];

const total = (rows, key) => rows.reduce((n, r) => n + r[key], 0);

// Every public institution, and any other with records for the year: admitted students and requests by status
const useInstitutions = (year) =>
  useQuery({
    queryKey: [...INSTITUTIONS_KEY, year],
    enabled: Boolean(year),
    queryFn: async () => {
      try {
        return (await bursariesApi.institutions(year)).data;
      } catch (error) {
        console.error(`Failed to fetch the institutions' bursaries for ${year}:`, error);
        toast.error(errorMessageOf(error, "Failed to fetch the institutions' bursaries."), { id: "institution-bursaries-error" });
        throw error;
      }
    },
  });

// Opens or closes an institution's bursary requests
const useSetWindow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ institutionId, open }) => bursariesApi.setWindowFor(institutionId, open),
    onSuccess: (_, { open }) => {
      toast.success(`Bursary requests ${open ? "opened" : "closed"}.`, { autoClose: 3000 });
      queryClient.invalidateQueries({ queryKey: INSTITUTIONS_KEY });
    },
    onError: (error) => {
      console.error("Failed to change the request window:", error);
      toast.error(errorMessageOf(error, "Failed to change the request window."));
    },
  });
};

const COLUMNS = [
  { key: "institution", label: "Institution", accessor: (r) => r.institution.name, className: "font-semibold text-slate-900" },
  { key: "requestsOpen", label: "Requests", accessor: (r) => (r.requestsOpen ? "Open" : "Closed"), render: (r) => <Tag color={r.requestsOpen ? "green" : "gray"}>{r.requestsOpen ? "Open" : "Closed"}</Tag> },
  { key: "admitted", label: "Admitted students", sortValue: (r) => r.admitted },
  { key: "pending", label: "Pending", sortValue: (r) => r.pending },
  { key: "granted", label: "Granted", sortValue: (r) => r.granted },
  { key: "rejected", label: "Rejected", sortValue: (r) => r.rejected },
];

// The HEC Admin's bursaries grouped by institution; one opens to its requests and students
export function InstitutionBursariesPage() {
  const navigate = useNavigate();
  const [year, setYear, years] = useYearParam();
  const { data = [], isPending, isError, refetch } = useInstitutions(year);
  const open = (row) => navigate(`${INSTITUTIONS_PATH}/${row.institution.id}?year=${year}`);
  const yearName = years.find((y) => y.value === year)?.label ?? "";

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Bursaries by institution"
        subtitle="The admitted students each institution uploaded and the bursary requests of its students"
        actions={<Select aria-label="Academic year" value={year} onChange={(e) => setYear(e.target.value)} options={years} className="w-44" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Admitted students" value={total(data, "admitted")} icon={<UsersIcon className="h-4 w-4" />} />
        <MetricCard title="Pending" value={total(data, "pending")} icon={<ClockIcon className="h-4 w-4" />} />
        <MetricCard title="Granted" value={total(data, "granted")} icon={<CheckCircleIcon className="h-4 w-4" />} />
        <MetricCard title="Rejected" value={total(data, "rejected")} icon={<XCircleIcon className="h-4 w-4" />} />
      </div>
      {isError ? (
        <EmptyState title="Could not load the institutions" onRefresh={refetch} />
      ) : (
        <Table cardView viewKey="bursary-institutions"
          columns={[...COLUMNS, { key: "open", label: "", exportable: false, render: (r) => <RowAction Icon={EyeIcon} label="View requests" onClick={() => open(r)} /> }]}
          data={data}
          rowKey={(r) => r.institution.id}
          onRowClick={open}
          isLoading={isPending}
          defaultSort={{ key: "pending", direction: "desc" }}
          searchPlaceholder="Search by institution"
          exportName={`bursaries-by-institution-${yearName}`}
          emptyTitle="No institutions found"
        />
      )}
    </Container>
  );
}

const TABS = [
  { value: "requests", label: "Requests" },
  { value: "students", label: "Admitted students" },
];

// One institution's bursary requests for the year, which the HEC Admin may grant or reject too, and its admitted students
export function InstitutionBursaryPage() {
  const { institutionId } = useParams();
  const [year, , years] = useYearParam();
  const [tab, setTab] = useState("requests");
  const { data, isPending } = useInstitutions(year);
  const setWindow = useSetWindow();
  const institution = data?.find((r) => r.institution.id === institutionId);
  const yearName = years.find((y) => y.value === year)?.label ?? "";

  if (isPending) return <Loading label="Loading the institution…" />;
  if (!institution) return <EmptyState title="This institution was not found" />;

  const name = institution.institution.name;
  const slug = `${name}-${yearName}`.replace(/\s+/g, "-");

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={`${INSTITUTIONS_PATH}?year=${year}`} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All institutions
      </Link>
      <PageHeader
        title={name}
        subtitle={`Academic year ${yearName} · ${institution.admitted.toLocaleString("en-US")} admitted students uploaded`}
        actions={
          <div className="flex items-center gap-3 text-sm text-slate-600">
            {institution.requestsOpen ? "Accepting requests" : "Requests closed"}
            <Switch
              checked={institution.requestsOpen}
              loading={setWindow.isPending}
              label={`Bursary requests for ${name}`}
              onChange={(open) => setWindow.mutate({ institutionId, open })}
            />
          </div>
        }
      />
      <Tabs tabs={TABS} value={tab} onChange={setTab} className="w-fit" />
      {tab === "requests" ? (
        <LoanRequestsTable
          list={(params) => bursariesApi.requestsOf(institutionId, params)}
          decide={bursariesApi.decide}
          params={{ institutionId, academicYearId: year }}
          exportName={`${slug}-bursary-requests`}
          counts={{ PENDING: institution.pending, GRANTED: institution.granted, REJECTED: institution.rejected }}
        />
      ) : (
        <AdmittedStudentsTable
          list={(params) => bursariesApi.studentsOf(institutionId, params)}
          params={{ institutionId, academicYearId: year }}
          exportName={`${slug}-admitted-students`}
        />
      )}
    </Container>
  );
}
